import React from 'react';
import { motion } from 'framer-motion';
import { brandInfo, contactInfo } from '../data/mock';
import { Music2, Instagram, Youtube, Facebook } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { label: 'About', href: '#about' },
      { label: 'Services', href: '#services' },
      { label: 'Releases', href: '#releases' },
      { label: 'Gallery', href: '#gallery' },
      { label: 'Contact', href: '#contact' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '#' },
      { label: 'Terms of Service', href: '#' },
      { label: 'Cookie Policy', href: '#' },
    ],
    artists: [
      { label: 'Submit Demo', href: '#contact' },
      { label: 'Artist Roster', href: '#' },
      { label: 'Collaborations', href: '#contact' },
    ],
  };

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-black pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Main Footer Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 pb-16 border-b border-white/10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a href="#" className="flex items-center gap-3 mb-6">
              <img
                src={brandInfo.logo}
                alt={brandInfo.name}
                className="h-14 w-14 rounded-full object-cover"
              />
            </a>
            <h3 className="font-display text-2xl font-bold text-white uppercase tracking-wider mb-2">
              {brandInfo.name}
            </h3>
            <p className="text-light-pink font-mono text-sm uppercase tracking-widest mb-6">
              {brandInfo.slogan}
            </p>
            <p className="text-white/60 text-sm leading-relaxed">
              South Africa's premier Deep House record label, bringing authentic sounds to the world.
            </p>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6">Company</h4>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-white/70 hover:text-light-pink transition-colors text-base"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Artists Links */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6">For Artists</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="https://voice-audition-app.preview.emergentagent.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-light-pink transition-colors text-base"
                >
                  Submit Demo
                </a>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('#')}
                  className="text-white/70 hover:text-light-pink transition-colors text-base"
                >
                  Artist Roster
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('#contact')}
                  className="text-white/70 hover:text-light-pink transition-colors text-base"
                >
                  Collaborations
                </button>
              </li>
            </ul>
          </div>

          {/* Contact & Socials */}
          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6">Connect</h4>
            <div className="space-y-4 mb-8">
              <p className="text-white/70 text-sm">
                {contactInfo.location}
              </p>
              <a href={`mailto:${contactInfo.email}`} className="text-white/70 hover:text-light-pink transition-colors text-sm block">
                {contactInfo.email}
              </a>
              <a href={`tel:${contactInfo.phone}`} className="text-white/70 hover:text-light-pink transition-colors text-sm block">
                {contactInfo.phone}
              </a>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href={contactInfo.socials.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FF0000] hover:text-white text-white transition-all"
                title="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href={contactInfo.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#1877F2] hover:text-white text-white transition-all"
                title="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={contactInfo.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] hover:text-white text-white transition-all"
                title="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={contactInfo.socials.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-black hover:text-white text-white transition-all border hover:border-[#69C9D0]"
                title="TikTok"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a
                href={contactInfo.socials.audiomack}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FFA500] hover:text-black text-white transition-all"
                title="Audiomack"
              >
                <Music2 className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} {brandInfo.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {footerLinks.legal.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-white/40 hover:text-white/70 transition-colors text-sm"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Large Brand Text */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-12 overflow-hidden"
        >
          <h2 className="font-display text-[6rem] md:text-[10rem] lg:text-[14rem] font-extrabold uppercase text-white/5 text-center leading-none select-none">
            Loxton
          </h2>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
